import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Button,
  Container,
  Typography,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Alert,
  Tooltip
} from '@mui/material';
import { Add as AddIcon, Settings as SettingsIcon, Search as SearchIcon, ContentCopy as CopyIcon } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import axios from '../utils/axios';
import CampusMinistryDetails from './CampusMinistryDetails';
import CampusMinistryForm from './CampusMinistryForm';
import { useError } from '../contexts/ErrorContext';
import {
  STUDENT_CATEGORY_LABELS,
  PAYMENT_STATUS_LABELS,
  PAYMENT_STATUS_COLORS,
  formatCurrency,
  balanceDue,
  fullName
} from '../utils/campusMinistryFields';

const CampusMinistryList = () => {
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [registrations, setRegistrations] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const { showError } = useError();

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await axios.get('/campus-ministry/events');
        const list = response.data || [];
        setEvents(list);
        const current = list.find(e => e.isCurrent) || list[0];
        setSelectedEventId(current ? current._id : '');
      } catch (error) {
        showError('Failed to load Campus Ministry events');
      }
    };
    fetchEvents();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchRegistrations = useCallback(async () => {
    try {
      const params = selectedEventId ? { eventId: selectedEventId } : {};
      const response = await axios.get('/campus-ministry', { params });
      setRegistrations(response.data || []);
    } catch (error) {
      setRegistrations([]);
      showError('Failed to fetch Campus Ministry registrations');
    }
  }, [selectedEventId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchRegistrations();
  }, [fetchRegistrations]);

  const selectedEvent = events.find(e => e._id === selectedEventId);
  const publicLink = selectedEvent ? `${window.location.origin}/campus-ministry/${selectedEvent.formType}/register` : '';

  const filteredRegistrations = registrations.filter(reg => {
    const searchLower = searchTerm.toLowerCase();
    return (
      fullName(reg).toLowerCase().includes(searchLower) ||
      (reg.email || '').toLowerCase().includes(searchLower)
    );
  });

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(publicLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) { /* clipboard unavailable */ }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" gutterBottom component="div" sx={{ mb: 0 }}>
          Campus Ministry
        </Typography>
        <Tooltip title="Events & Settings">
          <IconButton component={Link} to="/campus-ministry/settings" color="primary" aria-label="settings">
            <SettingsIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {events.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No events yet. Open <Link to="/campus-ministry/settings">Events &amp; Settings</Link> to create the first one (e.g. Fall Retreat {new Date().getFullYear()}).
        </Alert>
      )}

      <Paper sx={{ p: 2 }}>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', flex: 1 }}>
            <FormControl sx={{ minWidth: 220 }} size="small">
              <InputLabel id="cm-event-label">Event</InputLabel>
              <Select
                labelId="cm-event-label"
                value={selectedEventId}
                label="Event"
                onChange={(e) => setSelectedEventId(e.target.value)}
              >
                {events.map((ev) => (
                  <MenuItem key={ev._id} value={ev._id}>
                    {ev.name}{ev.isCurrent ? ' (Current)' : ''}
                  </MenuItem>
                ))}
                <MenuItem value="">All Events</MenuItem>
              </Select>
            </FormControl>
            <TextField
              size="small"
              placeholder="Search name or email"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                )
              }}
              sx={{ minWidth: 240 }}
            />
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setIsFormOpen(true)}
            disabled={events.length === 0}
          >
            New Manual Registration
          </Button>
        </Box>

        {selectedEvent && (
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              size="small"
              label={selectedEvent.registrationEnabled ? 'Registration Open' : 'Registration Closed'}
              color={selectedEvent.registrationEnabled ? 'success' : 'default'}
            />
            <Typography variant="body2" color="text.secondary">
              {filteredRegistrations.length} registration{filteredRegistrations.length === 1 ? '' : 's'}
            </Typography>
            {selectedEvent.isCurrent && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>Public link:</Typography>
                <Typography variant="body2" component="a" href={publicLink} target="_blank" rel="noreferrer">{publicLink}</Typography>
                <Tooltip title={copied ? 'Copied!' : 'Copy link'}>
                  <IconButton size="small" onClick={copyLink}><CopyIcon fontSize="small" /></IconButton>
                </Tooltip>
              </>
            )}
          </Box>
        )}

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Student</TableCell>
                {!selectedEventId && <TableCell>Event</TableCell>}
                <TableCell>Registered</TableCell>
                <TableCell>Balance</TableCell>
                <TableCell>Payment Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRegistrations.map((registration) => (
                <TableRow
                  key={registration._id}
                  onClick={() => setSelectedRegistration(registration)}
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                >
                  <TableCell>{fullName(registration)}</TableCell>
                  <TableCell>{registration.email}</TableCell>
                  <TableCell>{STUDENT_CATEGORY_LABELS[registration.studentCategory] || '—'}</TableCell>
                  {!selectedEventId && <TableCell>{registration.eventName}</TableCell>}
                  <TableCell>{registration.registrationDate ? new Date(registration.registrationDate).toLocaleDateString() : ''}</TableCell>
                  <TableCell>{formatCurrency(balanceDue(registration))}</TableCell>
                  <TableCell>
                    <Chip
                      label={PAYMENT_STATUS_LABELS[registration.payment?.status] || 'Balance Due'}
                      color={PAYMENT_STATUS_COLORS[registration.payment?.status] || 'warning'}
                      variant="filled"
                      sx={{ borderRadius: 2, fontWeight: 600 }}
                    />
                  </TableCell>
                </TableRow>
              ))}
              {filteredRegistrations.length === 0 && (
                <TableRow>
                  <TableCell colSpan={selectedEventId ? 6 : 7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No registrations found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {selectedRegistration && (
          <CampusMinistryDetails
            registration={selectedRegistration}
            open={Boolean(selectedRegistration)}
            onClose={() => setSelectedRegistration(null)}
            onUpdate={fetchRegistrations}
          />
        )}

        <CampusMinistryForm
          open={isFormOpen}
          onClose={() => setIsFormOpen(false)}
          onSuccess={fetchRegistrations}
          events={events}
          defaultEventId={selectedEventId}
        />
      </Paper>
    </Container>
  );
};

export default CampusMinistryList;
